/**
 * Knowledge Health — OEM-manual extraction coverage, gaps, and staleness.
 *
 * Reads GET /api/agentic/knowledge-health (backend/app/routes/agentic_knowledge.py):
 * a read-only aggregate view over oem_manuals / ai_extracted_manual_data /
 * asset_manuals — extraction coverage %, avg confidence, status/approval
 * breakdowns, gaps, and the most-stale manuals. On 403/404 → NotEnabled.
 */
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, BookOpen, FileWarning } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Heading,
  Icon,
  KpiCard,
  PageHeader,
  Section,
  Spinner,
  Stack,
  Text,
} from '@smms/design-system';
import { getKnowledgeHealth } from '../api/agentic';
import { isNotEntitled } from '../api/client';
import NotEnabled from '../components/NotEnabled';

type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const SEVERITY_BADGE: Record<string, BadgeVariant> = {
  high: 'danger',
  medium: 'warning',
  low: 'neutral',
};

const STATUS_ORDER = ['pending', 'processing', 'completed', 'failed'];
const APPROVAL_ORDER = ['PENDING', 'APPROVED', 'REJECTED'];

const STATUS_BADGE: Record<string, BadgeVariant> = {
  completed: 'success',
  processing: 'info',
  pending: 'neutral',
  failed: 'danger',
};

const APPROVAL_BADGE: Record<string, BadgeVariant> = {
  APPROVED: 'success',
  PENDING: 'neutral',
  REJECTED: 'danger',
};

/** Sentence-case a snake_case or UPPERCASE status key for display. */
function humanizeLabel(s: string): string {
  const lower = s.toLowerCase().replaceAll('_', ' ');
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** Known keys first (in the given order, only if present), then any others —
 * these are open records, a status/approval value may be absent or new. */
function orderedEntries(record: Record<string, number>, order: string[]): [string, number][] {
  const known: [string, number][] = order.filter(k => k in record).map(k => [k, record[k]]);
  const rest = Object.entries(record).filter(([k]) => !order.includes(k));
  return [...known, ...rest];
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatConfidence(avg: number | null): string {
  if (avg == null) return '—';
  return `${Math.round(avg * 100)}%`;
}

function formatDays(days: number | null): string {
  if (days == null) return '—';
  return `${days} day${days === 1 ? '' : 's'}`;
}

export default function KnowledgeHealthPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['agentic', 'knowledge-health'],
    queryFn: getKnowledgeHealth,
  });

  if (isLoading) {
    return (
      <div
        className="flex min-h-[60vh] items-center justify-center"
        data-testid="knowledge-health-loading"
      >
        <Spinner size="lg" label="Loading knowledge health" />
      </div>
    );
  }

  if (error) {
    if (isNotEntitled(error)) return <NotEnabled />;
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-8">
        <EmptyState
          variant="error"
          icon={<Icon as={AlertTriangle} size="lg" />}
          title="Couldn't load knowledge health"
          description="Something went wrong while loading knowledge base health. Try again."
          action={<Button onClick={() => void refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  if (!data) return <NotEnabled />;

  const { summary, extraction, gaps, staleness_items } = data;

  if (summary.total_manuals === 0) {
    return (
      <div className="mx-auto w-full max-w-5xl p-6">
        <Stack gap="lg">
          <PageHeader
            title="Knowledge Health"
            description="Extraction coverage, gaps, and staleness across your OEM-manual knowledge base."
          />
          <Card padding="lg">
            <EmptyState
              icon={<Icon as={BookOpen} size="lg" />}
              title="No manuals yet"
              description="Upload OEM manuals to start building extraction coverage over your knowledge base."
            />
          </Card>
        </Stack>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl p-6">
      <Stack gap="lg">
        <PageHeader
          title="Knowledge Health"
          description="Extraction coverage, gaps, and staleness across your OEM-manual knowledge base. Read-only."
        />

        <Section>
          <Card padding="md">
            <Stack gap="xs">
              <Text variant="kpi-label" tone="muted">
                Extraction coverage
              </Text>
              <Text variant="kpi-value" as="span" data-testid="coverage-stat">
                {summary.extraction_coverage_pct}%
              </Text>
              <Text variant="body-sm" tone="muted">
                {summary.with_extraction} of {summary.total_manuals} manuals have extracted data
              </Text>
            </Stack>
          </Card>
        </Section>

        <Section>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <KpiCard label="Avg confidence" value={formatConfidence(summary.avg_confidence)} />
            <KpiCard label="Never extracted" value={extraction.never_extracted} />
            <KpiCard label="Failed extractions" value={extraction.failed} />
            <KpiCard label="Unassigned to asset" value={gaps.manuals_unassigned_to_assets} />
          </div>
        </Section>

        <Section>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Card padding="md">
              <Stack gap="sm">
                <Heading level={2} className="text-base">
                  Extraction status
                </Heading>
                <ul className="space-y-2" data-testid="status-breakdown">
                  {orderedEntries(extraction.by_status, STATUS_ORDER).map(([status, count]) => (
                    <li key={status} className="flex items-center justify-between gap-2">
                      <Badge variant={STATUS_BADGE[status] ?? 'neutral'} size="sm">
                        {humanizeLabel(status)}
                      </Badge>
                      <Text variant="body-sm" className="font-medium tabular-nums">
                        {count}
                      </Text>
                    </li>
                  ))}
                </ul>
              </Stack>
            </Card>
            <Card padding="md">
              <Stack gap="sm">
                <Heading level={2} className="text-base">
                  Approval status
                </Heading>
                <ul className="space-y-2" data-testid="approval-breakdown">
                  {orderedEntries(extraction.by_approval, APPROVAL_ORDER).map(
                    ([status, count]) => (
                      <li key={status} className="flex items-center justify-between gap-2">
                        <Badge variant={APPROVAL_BADGE[status] ?? 'neutral'} size="sm">
                          {humanizeLabel(status)}
                        </Badge>
                        <Text variant="body-sm" className="font-medium tabular-nums">
                          {count}
                        </Text>
                      </li>
                    ),
                  )}
                </ul>
              </Stack>
            </Card>
          </div>
        </Section>

        <Section>
          <Stack gap="sm">
            <Heading level={2} className="text-base">
              Stalest manuals
            </Heading>
            {staleness_items.length === 0 ? (
              <Card padding="lg">
                <EmptyState
                  compact
                  icon={<Icon as={FileWarning} size="lg" />}
                  title="Nothing stale"
                  description="Every manual is extracted and up to date."
                />
              </Card>
            ) : (
              <Card padding="none">
                <table className="w-full text-left text-sm" data-testid="staleness-table">
                  <caption className="sr-only">
                    Manuals needing re-extraction, most stale first
                  </caption>
                  <thead>
                    <tr className="text-muted">
                      <th scope="col" className="px-4 py-2 font-medium">
                        Manual
                      </th>
                      <th scope="col" className="px-4 py-2 font-medium">
                        Type
                      </th>
                      <th scope="col" className="px-4 py-2 font-medium">
                        Uploaded
                      </th>
                      <th scope="col" className="px-4 py-2 font-medium">
                        Last extracted
                      </th>
                      <th scope="col" className="px-4 py-2 font-medium">
                        Days since
                      </th>
                      <th scope="col" className="px-4 py-2 font-medium">
                        Severity
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {staleness_items.map(item => (
                      <tr key={item.manual_id} data-testid="staleness-row">
                        <td className="px-4 py-2 font-medium">{item.filename}</td>
                        <td className="px-4 py-2">{item.manual_type ?? '—'}</td>
                        <td className="px-4 py-2">{formatWhen(item.uploaded_at)}</td>
                        <td className="px-4 py-2">{formatWhen(item.last_extracted_at)}</td>
                        <td className="px-4 py-2">{formatDays(item.days_since_extraction)}</td>
                        <td className="px-4 py-2">
                          <Badge variant={SEVERITY_BADGE[item.severity] ?? 'neutral'} size="sm">
                            {humanizeLabel(item.severity)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </Stack>
        </Section>
      </Stack>
    </div>
  );
}
